import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/client';
import { RubyIcon, FloatIcon } from './CurrencyIcons';
import Avatar from './Avatar';
import { Medal } from './Glyph';
import AlertModal from './AlertModal';
import { iconUrl } from '../utils/assets';
import { useLocale } from '../i18n/LocaleContext';
import { celebrate } from '../utils/fx';
import { shareToStory } from '../utils/telegram';
import { useGameAlert } from '../hooks/useGameAlert';
import { useVisibleInterval } from '../hooks/useVisibleInterval';

const LEAGUE_COLORS = {
  bronze: '#cd7f32',
  silver: '#c4b5a0',
  gold: '#ffd166',
  platinum: '#e8b86d',
  diamond: '#ffe6b0',
  master: '#ff6b35',
};

const FRAME_LABEL = {
  frame_season_master: { ru: 'Рамка Мастера', en: 'Master Frame' },
  frame_season_gold: { ru: 'Золотая рамка', en: 'Gold Frame' },
  frame_season_silver: { ru: 'Серебряная рамка', en: 'Silver Frame' },
};

const formatLeft = (sec, lang) => {
  if (sec == null) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (lang === 'en') {
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m ${s}s`;
  }
  if (d > 0) return `${d}д ${h}ч`;
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}м ${s}с`;
};

const leagueLabel = (id, lang) => {
  const ru = {
    bronze: 'Бронза',
    silver: 'Серебро',
    gold: 'Золото',
    platinum: 'Платина',
    diamond: 'Алмаз',
    master: 'Мастер',
  };
  const en = {
    bronze: 'Bronze',
    silver: 'Silver',
    gold: 'Gold',
    platinum: 'Platinum',
    diamond: 'Diamond',
    master: 'Master',
  };
  return (lang === 'en' ? en : ru)[id] || id;
};

const frameLabel = (code, lang) => {
  if (!code) return lang === 'en' ? 'No frame' : 'Без рамки';
  const row = FRAME_LABEL[code];
  if (!row) return code;
  return lang === 'en' ? row.en : row.ru;
};

const fmtGap = (v) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1000) return n.toFixed(0);
  return n.toFixed(n >= 10 ? 1 : 2);
};

const Top = () => {
  const { t, lang } = useLocale();
  const { alert, closeAlert, showInfo } = useGameAlert();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('global');
  const [loading, setLoading] = useState(true);
  const [leagueOpen, setLeagueOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [loadedAt, setLoadedAt] = useState(Date.now());
  const [refreshing, setRefreshing] = useState(false);
  const loadedAtRef = useRef(loadedAt);
  const listRef = useRef(null);
  // Место с прошлого захода — фиксируем один раз за монтирование, иначе
  // стрелка «+3 места» исчезнет на первом же тихом обновлении.
  const prevRankRef = useRef(null);
  const prevRankRead = useRef(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const payload = await api.leaderboard();
      setData(payload);
      setLoadedAt(Date.now());
    } catch (_) {
      /* оставляем прошлые данные — пустой топ хуже устаревшего */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const payload = await api.leaderboard();
        if (!cancelled) {
          setData(payload);
          setLoadedAt(Date.now());
        }
      } catch (_) {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Топ — живой экран: пока он открыт, места меняются у всех. Тихо обновляем
   * раз в минуту и сразу при возврате в приложение, чтобы игрок не смотрел
   * на устаревшую таблицу и не думал, что его очки не засчитались.
   */
  // Один путь вместо двух: хук и тикает раз в минуту на видимом экране, и
  // дёргает колбэк на возврате в приложение. Проверка свежести оставлена та же,
  // что была у отдельного обработчика visibilitychange, — иначе быстрый уход и
  // возврат слал бы лишний запрос.
  useVisibleInterval(() => {
    if (Date.now() - loadedAtRef.current > 15000) load({ silent: true });
  }, 60000);

  // Обратный отсчёт сезона считается от даты окончания, так что в фоне тикать
  // незачем — на возврате хук сразу пересчитает.
  useVisibleInterval(() => setTick((x) => x + 1), 1000);

  useEffect(() => {
    const league = data?.me?.league;
    if (!league) return;
    const key = 'tm_last_league';
    const prev = sessionStorage.getItem(key);
    if (prev && prev !== league) celebrate('league');
    if (league) sessionStorage.setItem(key, league);
  }, [data?.me?.league]);

  useEffect(() => {
    loadedAtRef.current = loadedAt;
  }, [loadedAt]);

  /**
   * «+3 места с прошлого захода» — считаем от места, сохранённого в прошлой
   * сессии этого же сезона. Читаем ровно один раз, потом сразу переписываем
   * значение: иначе стрелка врёт при каждом тихом обновлении.
   */
  useEffect(() => {
    const rank = data?.me?.rank;
    const season = data?.season?.number;
    if (!rank || season == null) return;
    const key = `tm_rank_s${season}`;
    if (!prevRankRead.current) {
      prevRankRead.current = true;
      const raw = localStorage.getItem(key);
      const prev = raw ? parseInt(raw, 10) : NaN;
      prevRankRef.current = Number.isFinite(prev) ? prev : null;
    }
    try {
      localStorage.setItem(key, String(rank));
    } catch (_) {
      /* приватный режим — просто без стрелки */
    }
  }, [data?.me?.rank, data?.season?.number]);

  const rankMove = useMemo(() => {
    const prev = prevRankRef.current;
    const now = data?.me?.rank;
    if (!prev || !now || prev === now) return null;
    // Меньше номер = выше место.
    return { up: now < prev, n: Math.abs(prev - now) };
  }, [data?.me?.rank]);

  const secondsLeft = useMemo(() => {
    void tick;
    const base = data?.season?.seconds_left;
    if (base == null) return null;
    const elapsed = Math.floor((Date.now() - loadedAt) / 1000);
    return Math.max(0, base - elapsed);
  }, [data, tick, loadedAt]);

  const urgent = secondsLeft != null && secondsLeft < 86400;
  const me = data?.me;
  const pct = me?.league_progress_pct ?? 0;
  const podium = useMemo(() => (data?.items || []).slice(0, 3), [data?.items]);
  const listRows = useMemo(() => {
    const rows = tab === 'nearby' ? data?.nearby || [] : data?.items || [];
    if (tab !== 'global') return rows;
    return rows.filter((r) => r.rank > 3);
  }, [data?.nearby, data?.items, tab]);
  // Липкая строка «ВЫ» нужна, чтобы своё место было под рукой при прокрутке
  // чужих. Но если своя строка и так стоит первой в списке, получаются две
  // одинаковые строки подряд с одним и тем же «#4» — прячем дубль.
  const meVisibleAtTop = useMemo(() => {
    if (!me?.rank) return false;
    return listRows.slice(0, 3).some((r) => r.rank === me.rank);
  }, [listRows, me?.rank]);
  // Очки лидера — база для полосок «доля от первого места» в строках.
  const leaderPoints = useMemo(() => {
    const first = (data?.items || [])[0];
    const n = first ? parseFloat(first.points) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [data?.items]);
  const topReward = (data?.rank_rewards || [])[0];
  const last = data?.last_season;
  const leagues = data?.leagues || Object.keys(LEAGUE_COLORS);

  const seasonTitle = data?.season
    ? lang === 'en'
      ? data.season.title_en || `${t('season')} ${data.season.number}`
      : data.season.title_ru || data.season.title || `${t('season')} ${data.season.number}`
    : t('seasonTitle');

  const projectedTitle =
    lang === 'en'
      ? me?.projected_title_label_en || me?.projected_title || ''
      : me?.projected_title_label || me?.projected_title || '';

  const gapLabel = useMemo(() => {
    if (!me?.gap_above || !me?.gap_above_rank) return null;
    const g = fmtGap(me.gap_above);
    if (!g) return null;
    return lang === 'en'
      ? `−${g} pts to #${me.gap_above_rank}`
      : `−${g} очков до #${me.gap_above_rank}`;
  }, [me, lang]);

  return (
    <main className={`top-screen season-screen screen-enter ${urgent ? 'season-urgent' : ''}`}>
      <section className={`season-hero glass-panel ${urgent ? 'is-urgent' : ''}`}>
        <div className="season-hero-glow" aria-hidden />
        {urgent ? <div className="season-urgent-banner">{t('seasonUrgent')}</div> : null}
        <div className="season-hero-top">
          <div className="season-badge">
            <FloatIcon src={iconUrl('trophy.png')} size={28} eager animated native />
            <span>S{data?.season?.number || '—'}</span>
          </div>
          <div className={`season-timer-pill ${urgent ? 'is-urgent' : ''}`}>
            <span className="season-timer-label">{t('seasonEndsIn')}</span>
            <strong>{formatLeft(secondsLeft, lang)}</strong>
          </div>
        </div>
        <h1 className="season-hero-title">{seasonTitle}</h1>
        <p className="season-hero-sub">{t('seasonWeekly')}</p>
        <div className="season-hero-meta">
          <div className="season-meta-cell">
            <span className="season-meta-val">{data?.season?.players || 0}</span>
            <span className="season-meta-lbl">{t('seasonPlayers')}</span>
          </div>
          <div className="season-meta-cell">
            <span className="season-meta-val">#{me?.rank ?? '—'}</span>
            <span className="season-meta-lbl">{t('yourPlace')}</span>
          </div>
          <div className="season-meta-cell">
            <span className="season-meta-val amber-balance">
              <RubyIcon size={14} /> {topReward?.amber ?? '—'}
            </span>
            <span className="season-meta-lbl">{t('top1Ruby')}</span>
          </div>
        </div>
      </section>

      {tab === 'global' && podium.length > 0 ? (
        <section className="podium-stage" aria-label={t('top100')}>
          <span className="podium-rays" aria-hidden />
          <span className="podium-floor" aria-hidden />
          {/* Порядок в разметке — 2 · 1 · 3, как на настоящем пьедестале. */}
          {[podium[1], podium[0], podium[2]].filter(Boolean).map((row) => {
            const place = row.rank;
            const mine = Boolean(me && row.rank === me.rank);
            return (
              <div
                key={row.user_id}
                className={`podium-slot podium-${place} ${mine ? 'is-me' : ''}`}
                style={{ '--lg': LEAGUE_COLORS[row.league] || '#aaa' }}
              >
                <Medal
                  place={place}
                  crown={place === 1}
                  size={place === 1 ? 30 : 24}
                  className="podium-medal"
                />
                <div className="podium-avatar-ring">
                  <span className="podium-ring-spin" aria-hidden />
                  <Avatar
                    userData={{ photo_url: row.photo_url, first_name: row.name }}
                    size={place === 1 ? 64 : 50}
                    className={`top-avatar frame-${row.frame || 'none'}`}
                  />
                  {mine ? <span className="you-badge podium-you">{t('you')}</span> : null}
                </div>
                <div className="podium-name">{row.name}</div>
                <div className="podium-league">{leagueLabel(row.league, lang)}</div>
                <div className="podium-pedestal">
                  <span className="podium-place">{place}</span>
                  <span className="podium-pts">{row.points}</span>
                </div>
              </div>
            );
          })}
        </section>
      ) : null}

      <div className="seg-tabs season-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'global'}
          className={`seg-tab ${tab === 'global' ? 'is-active' : ''}`}
          onClick={() => setTab('global')}
        >
          {t('top100')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'nearby'}
          className={`seg-tab ${tab === 'nearby' ? 'is-active' : ''}`}
          onClick={() => setTab('nearby')}
        >
          {t('nearby')}
        </button>
      </div>

      {loading ? (
        <div className="upgrades-list season-list" aria-hidden>
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="top-row-skeleton" key={i}>
              <span className="sk-rank" />
              <span className="sk-avatar" />
              <span className="sk-lines">
                <i />
                <i />
              </span>
              <span className="sk-pts" />
            </div>
          ))}
        </div>
      ) : null}

      <div className="upgrades-list season-list" ref={listRef}>
        {/* Своя строка всегда под рукой: прилипает к верху списка, пока крутишь
            чужие места, и остаётся кликабельной ссылкой на вкладку «рядом». */}
        {!loading && me?.rank && !meVisibleAtTop ? (
          <button
            type="button"
            className="top-sticky-me"
            onClick={() => {
              // Если своя строка есть в текущем списке — просто доводим до неё,
              // и только иначе переключаем вкладку.
              const row = listRef.current?.querySelector('.top-row.is-me');
              if (row) {
                row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
              }
              setTab(tab === 'global' ? 'nearby' : 'global');
            }}
          >
            <span className="rank-num sticky-rank">#{me.rank}</span>
            <span className="sticky-me-text">
              <span className="sticky-me-name">
                {t('you')}
                {rankMove ? (
                  <span className={`rank-move ${rankMove.up ? 'is-up' : 'is-down'}`}>
                    {rankMove.up ? '▲' : '▼'}
                    {rankMove.n}
                  </span>
                ) : null}
                {refreshing ? <span className="sticky-live" aria-hidden /> : null}
              </span>
              <span className="sticky-me-sub">
                <span style={{ color: LEAGUE_COLORS[me.league] || undefined }}>
                  {leagueLabel(me.league, lang)}
                </span>
                {gapLabel ? ` · ${gapLabel}` : ''}
              </span>
            </span>
            <span className="sticky-me-pts">{me.points}</span>
            <span className="sticky-me-switch">{tab === 'global' ? t('nearby') : t('top100')}</span>
          </button>
        ) : null}

        {/* With ≤3 players the podium already shows everyone — «nobody here yet»
            under a full podium just looks broken. */}
        {!loading && !listRows.length && !(tab === 'global' && podium.length > 0) ? (
          <div className="inline-message">{t('topEmpty')}</div>
        ) : null}

        {listRows.map((row) => {
          const isMe = data?.me && row.rank === data.me.rank;
          let deltaHint = null;
          if (tab === 'nearby' && isMe && row.delta_above) {
            const g = fmtGap(row.delta_above);
            if (g) {
              deltaHint =
                lang === 'en'
                  ? `−${g} to #${row.rank - 1}`
                  : `−${g} до #${row.rank - 1}`;
            }
          } else if (tab === 'nearby' && me && row.rank === me.rank - 1 && me.gap_above) {
            const g = fmtGap(me.gap_above);
            if (g) {
              deltaHint =
                lang === 'en' ? `+${g} ahead` : `+${g} впереди`;
            }
          }
          const rowTitle =
            lang === 'en'
              ? row.title_label_en || row.title_label || row.title
              : row.title_label || row.title_label_en || row.title;
          // Доля от лидера сезона — видно не только место, но и отрыв.
          const share = leaderPoints
            ? Math.max(2, Math.min(100, (parseFloat(row.points) / leaderPoints) * 100))
            : 0;
          return (
            <div
              className={`upgrades-item top-row soft-card ${isMe ? 'is-me' : ''} ${
                row.rank <= 3 ? `is-podium podium-${row.rank}` : ''
              }`}
              key={`${row.rank}-${row.user_id}`}
              style={{ '--lg': LEAGUE_COLORS[row.league] || '#aaa' }}
            >
              <span className={`rank-num ${row.rank <= 3 ? `podium-${row.rank}` : ''}`}>
                {row.rank <= 3 ? (
                  <Medal place={row.rank} size={18} className="rank-medal" />
                ) : null}
                #{row.rank}
              </span>
              <span className="top-ava-wrap">
                <Avatar
                  userData={{ photo_url: row.photo_url, first_name: row.name }}
                  size={38}
                  className={`top-avatar frame-${row.frame || 'none'}`}
                />
              </span>
              <div className="item-text">
                {/* Frame class stays off the name — it belongs to the avatar, and
                    on a text box its ring decorations became stray rectangles. */}
                <span className={`upgrades-item-info-name name-${row.frame || 'none'}`}>
                  {row.name}
                  {isMe ? <span className="you-badge">{t('you')}</span> : null}
                  {row.prestige ? <span className="level-badge">★{row.prestige}</span> : null}
                </span>
                <span className="item-desc top-row-meta">
                  <span className="top-league-chip">{leagueLabel(row.league, lang)}</span>
                  {rowTitle ? <span className="top-title-chip">{rowTitle}</span> : null}
                  {row.clan_tag ? (
                    <span className="top-clan-chip">
                      [{row.clan_tag}]
                    </span>
                  ) : null}
                </span>
                <span className="top-share-track" aria-hidden>
                  <i style={{ width: `${share}%` }} />
                </span>
                {deltaHint ? <span className="season-delta">{deltaHint}</span> : null}
              </div>
              <span className="frens-item-info-balance season-pts">{row.points}</span>
            </div>
          );
        })}
      </div>

      {me ? (
        <section className="season-reward-strip glass-panel">
          <div className="season-reward-head">
            <span className="season-reward-eyebrow">{t('yourProjectedReward')}</span>
            <strong className="season-reward-place">
              {t('placeShort')} #{me.rank ?? '—'}
              {me.projected_amber > 0 ? (
                <span className="season-reward-arrow">
                  → ~{me.projected_amber} <RubyIcon size={14} />
                </span>
              ) : (
                <span className="season-reward-arrow muted">{t('outsideRewards')}</span>
              )}
            </strong>
          </div>
          <div className="season-prize-strip">
            <div className="season-prize-cell">
              <span className="season-prize-icon amber-balance">
                <RubyIcon size={18} />
              </span>
              <b>{me.projected_amber || 0}</b>
              <span>{t('rubies')}</span>
            </div>
            <div className="season-prize-cell">
              <span className={`season-prize-frame frame-${me.projected_frame || 'none'}`} aria-hidden />
              <b>{me.projected_frame ? '✓' : '—'}</b>
              <span>{frameLabel(me.projected_frame, lang)}</span>
            </div>
            <div className="season-prize-cell">
              <span className="season-prize-icon season-title-ico">★</span>
              <b className="season-prize-title">{projectedTitle || '—'}</b>
              <span>{t('seasonTitlePrize')}</span>
            </div>
          </div>
          {gapLabel ? <div className="season-gap-pill">{gapLabel}</div> : null}
        </section>
      ) : null}

      {last ? (
        <section className="season-last glass-panel">
          <div className="season-last-head">
            <FloatIcon src={iconUrl('trophy.png')} size={20} eager animated native />
            <span>
              {t('lastSeason')} · S{last.season_number}
            </span>
          </div>
          <div className="season-last-grid">
            <div>
              <b>#{last.rank}</b>
              <span>{t('yourPlace')}</span>
            </div>
            <div>
              <b className="amber-balance">
                <RubyIcon size={13} /> {last.amber}
              </b>
              <span>{t('rubies')}</span>
            </div>
            <div>
              <b className="frame-chip">
                <span className={`tier-frame-dot ${last.frame ? 'is-on' : ''}`} aria-hidden />
              </b>
              <span>{frameLabel(last.frame, lang)}</span>
            </div>
          </div>
        </section>
      ) : null}

      <section className="season-ladder glass-panel" aria-label={t('seasonLeaguesTitle')}>
        <div className="season-ladder-head">
          <span>{t('leagueLadder')}</span>
          <button type="button" className="league-info-mini" onClick={() => setLeagueOpen(true)}>
            <FloatIcon src={iconUrl('bulb.png')} size={14} eager animated native />
          </button>
        </div>
        <div className="season-ladder-track">
          {leagues.map((id, i) => {
            const active = me?.league === id;
            const passed =
              me?.league && leagues.indexOf(me.league) >= i;
            return (
              <div
                key={id}
                className={`season-ladder-step ${active ? 'is-active' : ''} ${passed ? 'is-passed' : ''}`}
                style={{ '--lg': LEAGUE_COLORS[id] || '#aaa' }}
              >
                <span className="season-ladder-dot" />
                <span className="season-ladder-name">{leagueLabel(id, lang)}</span>
              </div>
            );
          })}
        </div>
      </section>

      {me ? (
        <section className="me-rank me-rank-rich glass-panel season-me-card">
          <div className="season-me-head">
            <button type="button" className="league-badge-btn" onClick={() => setLeagueOpen(true)}>
              <span
                className="league-dot"
                style={{ background: LEAGUE_COLORS[me.league] || '#aaa' }}
              />
              <span className="league-name">{leagueLabel(me.league, lang)}</span>
              <FloatIcon src={iconUrl('bulb.png')} size={15} eager animated native />
            </button>
            <span className="season-me-pts">
              {me.points} <small>{t('points')}</small>
            </span>
          </div>

          <div className="season-me-grid">
            <div className="season-me-stat">
              <span className="season-me-stat-val">#{me.rank ?? '—'}</span>
              <span className="season-me-stat-lbl">{t('rank')}</span>
            </div>
            <div className="season-me-stat">
              <span className="season-me-stat-val amber-balance">
                <RubyIcon size={13} /> {me.amber ?? 0}
              </span>
              <span className="season-me-stat-lbl">{t('rubies')}</span>
            </div>
            <div className="season-me-stat">
              <span className="season-me-stat-val">{pct}%</span>
              <span className="season-me-stat-lbl">
                {me.next_league ? `→ ${leagueLabel(me.next_league, lang)}` : t('topLeague')}
              </span>
            </div>
          </div>

          <div className="league-progress season-progress" aria-hidden>
            <div className="league-progress-fill" style={{ transform: `scaleX(${pct / 100})` }} />
          </div>
          {me.next_league_points ? (
            <div className="item-desc league-progress-label">
              {t('toNextLeague')}: {me.next_league_points} {t('points')}
            </div>
          ) : null}
          {me.clan_tag ? (
            <div className="item-desc clan-under-name season-clan">
              [{me.clan_tag}]{me.clan_name ? ` ${me.clan_name}` : ''}
            </div>
          ) : null}

          <button
            type="button"
            className="action-btn stars soft-btn season-share"
            onClick={() => {
              const text =
                lang === 'en'
                  ? `Tap Mars · ${leagueLabel(me.league, lang)} · #${me.rank ?? '—'} · ${me.points} pts`
                  : `Tap Mars · ${leagueLabel(me.league, lang)} · #${me.rank ?? '—'} · ${me.points} очков`;
              const media = 'https://tap-mars.space/assets/story-mars.jpg';
              const appLink = 'https://t.me/Tap_Mars_bot/app';

              // Real Stories only — do NOT fall back to chat share (that is not a story).
              const ok =
                shareToStory(media, {
                  text,
                  widget_link: { url: appLink, name: 'Tap Mars' },
                }) ||
                shareToStory(media, { text }) ||
                shareToStory(media);

              if (ok) return;

              showInfo({
                icon: 'bulb.png',
                title: t('storyUnsupported'),
                body:
                  lang === 'en'
                    ? 'This Telegram build cannot open the story editor. Try the official Telegram app, or update it.'
                    : 'Этот клиент не открывает редактор сторис. Попробуй официальный Telegram или обнови приложение.',
                confirmLabel: t('gotIt'),
              });
            }}
          >
            {t('shareStory')}
          </button>
        </section>
      ) : null}

      <section className="season-rewards-ladder glass-panel">
        <div className="section-label" style={{ margin: 0 }}>
          {t('endSeasonRubies')}
        </div>
        <div className="season-rewards-scroll">
          {(() => {
            const rewards = data?.rank_rewards || [];
            const myTier = rewards.find((r) => me?.rank && me.rank <= r.max_rank);
            return rewards.map((r) => {
              const mine = myTier && r.max_rank === myTier.max_rank;
              const title =
                lang === 'en' ? r.title_label_en || r.title : r.title_label || r.title;
              return (
                <div
                  key={r.max_rank}
                  className={`season-reward-tier ${mine ? 'is-mine' : ''}`}
                >
                  <span className="season-reward-tier-rank">≤#{r.max_rank}</span>
                  <span className="amber-balance">
                    <RubyIcon size={12} /> {r.amber}
                  </span>
                  <span className="season-reward-tier-frame">
                    <span className={`tier-frame-dot ${r.frame ? 'is-on' : ''}`} aria-hidden />
                  </span>
                  <span className="season-reward-tier-title">{title}</span>
                </div>
              );
            });
          })()}
        </div>
      </section>

      <AlertModal
        open={leagueOpen}
        icon="trophy.png"
        title={t('seasonLeaguesTitle')}
        body={t('seasonLeaguesBody')}
        onClose={() => setLeagueOpen(false)}
        confirmLabel={t('gotIt')}
      >
        <div className="league-info-list">
          {(data?.leagues_info || []).map((L) => (
            <div className="league-info-row" key={L.id}>
              <span className="league-dot" style={{ background: LEAGUE_COLORS[L.id] }} />
              <div className="item-text">
                <span className="upgrades-item-info-name">{leagueLabel(L.id, lang)}</span>
                <span className="item-desc">
                  {lang === 'en' ? L.percentile_en : L.percentile} —{' '}
                  {lang === 'en' ? L.desc_en : L.desc}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="section-label" style={{ marginTop: 8 }}>
          {t('endSeasonRubies')}
        </div>
        <div className="league-info-list">
          {(data?.rank_rewards || []).map((r) => (
            <div className="league-info-row" key={r.max_rank}>
              <span className="rank-num">≤{r.max_rank}</span>
              <span className="amber-balance">
                <RubyIcon size={14} /> {r.amber}
              </span>
              <span className="item-desc">
                {lang === 'en' ? r.title_label_en || r.title : r.title_label || r.title}
              </span>
            </div>
          ))}
        </div>
      </AlertModal>
      <AlertModal open={Boolean(alert)} {...(alert || {})} onClose={closeAlert} />
    </main>
  );
};

export default Top;
